import { useCallback, useEffect, useMemo, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Reveal } from '@/components/Reveal';
import { OrderedGallery } from '@/components/OrderedGallery';
import {
  getPublic,
  readPublicCache,
  toAssetUrl,
  writePublicCache,
} from '@/data/runtimeApi';

const getInitialImages = () => {
  const cachedImages = readPublicCache('nosotros');
  return Array.isArray(cachedImages) ? cachedImages : [];
};

export default function Nosotros() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [images, setImages] = useState(getInitialImages);
  const [loading, setLoading] = useState(() => getInitialImages().length === 0);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;

    getPublic('/nosotros')
      .then(({ data }) => {
        if (!alive) return;
        if (!Array.isArray(data?.items)) {
          throw new Error('La API pública de Nosotros devolvió un formato inválido.');
        }

        // An empty array is authoritative: the admin intentionally cleared it.
        const nextImages = data.items.map((item) => toAssetUrl(item.src));
        setImages(nextImages);
        writePublicCache('nosotros', nextImages);
        setLoadError(false);
      })
      .catch((error) => {
        if (!alive) return;
        console.error('[public nosotros] No se pudo cargar la galería administrada.', error);
        setLoadError(true);
        // Preserve only a previous successful DB snapshot. Never show the
        // bundled preview gallery as an emergency fallback.
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const slides = useMemo(() => images.map((src) => ({ src })), [images]);

  // Keep React's controlled index synchronized with the lightbox's own
  // navigation. Without this, a later page re-render can send the stale
  // opening index back to the lightbox and make it jump to an older image.
  const handleLightboxView = useCallback(({ index: currentIndex }) => {
    if (Number.isInteger(currentIndex)) {
      setIndex(currentIndex);
    }
  }, []);

  useEffect(() => {
    if (slides.length === 0) {
      setOpen(false);
      setIndex(0);
      return;
    }

    if (index >= slides.length) {
      setIndex(slides.length - 1);
    }
  }, [index, slides.length]);

  return (
    <div
      className="text-[#58677A]"
      data-testid="nosotros-page"
      style={{
        backgroundColor: '#EFECE7',
        backgroundImage: "url('/assets/backgrounds/nosotros-bg.jpg')",
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
      }}
    >
      {/* Gallery + text in a single section */}
      <section className="py-12 sm:py-16 lg:py-20" data-testid="nosotros-content-section">
        <div className="container-link">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] xl:grid-cols-[1fr_300px] gap-10 lg:gap-14">
            {/* LEFT: ordered masonry gallery */}
            <div>
              <Reveal>
                {loading && images.length === 0 && (
                  <div className="min-h-[280px] rounded-2xl bg-white/35 animate-pulse" aria-label="Cargando galería" />
                )}
                {!loading && loadError && images.length === 0 && (
                  <p className="rounded-2xl border border-[#58677A]/15 bg-white/50 px-5 py-4 text-sm text-[#58677A]/75">
                    La galería no está disponible temporalmente.
                  </p>
                )}
                <OrderedGallery
                  images={images}
                  variant="about"
                  altPrefix="Momento Link"
                  testId="about-masonry-grid"
                  itemTestIdPrefix="about-gallery-item"
                  onImageClick={(_src, originalIndex) => {
                    setIndex(originalIndex);
                    setOpen(true);
                  }}
                />
              </Reveal>
            </div>

            {/* RIGHT: Institutional text - narrow vertical column */}
            <aside className="lg:sticky lg:top-28 self-start" data-testid="nosotros-text-block">
              <Reveal delay={0.1}>
                <div
                  className="text-[#58677A] leading-[1.55] text-[15px] sm:text-base lg:text-[17px]"
                  style={{ fontFamily: 'Gotham, sans-serif', fontWeight: 400 }}
                >
                  <p className="mb-5">
                    En LINK creemos que las experiencias tienen el poder de conectar marcas con
                    personas de una manera real y memorable. Más que ejecutar eventos o
                    desarrollar producciones visuales, construimos ideas con intención,
                    estrategia y creatividad para generar impacto en cada interacción.
                  </p>
                  <p>
                    Creamos experiencias únicas que combinan diseño, producción, comunicación y
                    tecnología para transformar espacios en momentos que se viven, se recuerdan y
                    se comparten.
                  </p>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        on={{ view: handleLightboxView }}
        controller={{ closeOnBackdropClick: true }}
        styles={{ container: { background: 'rgba(15,15,15,0.96)' } }}
      />
    </div>
  );
}
